import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import qz from "qz-tray";
import TicketView from "../components/TicketView";
import { connectQz, openDrawer, qzPrintPdfBase64 } from "./qz";
import { api } from "./api";
import { toast } from "sonner";

// Estaciones registradas por SecurityContext para resolver el modo de impresión del equipo actual.
let STATIONS = [];
export function registerStations(list) { STATIONS = Array.isArray(list) ? list : []; }
function currentStation() {
  try { const id = localStorage.getItem("pos_station_id"); return STATIONS.find((s) => s.id === id) || null; } catch { return null; }
}

const widthOf = (format) => (format === "58mm" ? 58 : 80);
// Área imprimible real de las térmicas (80mm → 72mm, 58mm → 48mm)
const printableOf = (format) => (format === "58mm" ? 48 : 72);

function wrapHtml(bodyInner, widthMm, fit = false) {
  return `<!doctype html><html><head><meta charset="utf-8"><title>Ticket</title><style>
    @page { size: ${widthMm}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; width: ${widthMm}mm; max-width: ${widthMm}mm; overflow: hidden; font-family: 'Helvetica Neue', Arial, sans-serif; color: #000; }
    table { width: 100%; border-collapse: collapse; }
    ${fit ? `body > div, #ticket-print, #ticket-preview { width: ${widthMm}mm !important; max-width: ${widthMm}mm !important; padding: 1mm 2mm !important; margin: 0 !important; }` : ""}
  </style></head><body>${bodyInner}</body></html>`;
}

function docToInner(doc, company, format) {
  return renderToStaticMarkup(React.createElement(TicketView, { doc, company, format }));
}

export function buildTicketHTML(doc, company, format) {
  return wrapHtml(docToInner(doc, company, format), widthOf(format));
}

// Motor de impresión por ventana emergente (respaldo del navegador).
export function popupPrintHTML(html) {
  const win = window.open("", "_blank", "width=420,height=680");
  if (!win) { alert("Permite las ventanas emergentes para poder imprimir el ticket."); return false; }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const doPrint = () => { try { win.focus(); win.print(); } catch { /* noop */ } };
  const imgs = win.document.images;
  if (imgs && imgs.length) {
    let pending = imgs.length, fired = false;
    const go = () => { if (fired) return; fired = true; doPrint(); };
    for (const im of imgs) {
      if (im.complete) { if (--pending === 0) go(); }
      else { im.onload = im.onerror = () => { if (--pending === 0) go(); }; }
    }
    setTimeout(go, 4000); // timeout de seguridad para el logo
  } else {
    setTimeout(doPrint, 200);
  }
  return true;
}

async function qzPrintHtml(printer, html, widthMm) {
  await connectQz();
  const config = qz.configs.create(printer || undefined, {
    units: "mm", size: { width: widthMm }, margins: 0, orientation: "portrait",
    scaleContent: true, rasterize: true, density: 203, fallbackDensity: 203, interpolation: "bicubic",
  });
  return qz.print(config, [{ type: "pixel", format: "html", flavor: "plain", data: html, options: { pageWidth: widthMm, pageHeight: 0 } }]);
}
function qzHtml(doc, company, format, w) { return wrapHtml(docToInner(doc, company, format), w, true); }

let warned = 0;
function warnNoQz(e) {
  if (Date.now() - warned < 15000) return;
  warned = Date.now();
  toast.warning("QZ Tray no está disponible en este equipo; se abrió el diálogo del navegador como respaldo.", { description: e?.message ? String(e.message).slice(0, 120) : undefined });
}

// Impresión: exclusivamente a través de QZ Tray (impresora de la estación o predeterminada del sistema).
export async function printTicketDoc(doc, company, format = "80mm", opts = {}) {
  const { qzPrinter = null } = opts;
  const st = currentStation();
  const printer = qzPrinter || st?.qz_printer || null;
  const qzCashDrawer = opts.qzCashDrawer !== undefined ? !!opts.qzCashDrawer : false;
  const html = buildTicketHTML(doc, company, format);
  const w = Number(st?.print_width_mm) || printableOf(format);
  try {
    await qzPrintHtml(printer, qzHtml(doc, company, format, w), w);
    if (qzCashDrawer) { try { await openDrawer(printer); } catch { /* noop */ } }
    return "qz";
  } catch (e) {
    warnNoQz(e);
    popupPrintHTML(html);
    return "popup";
  }
}

// Imprime la factura (CFDI) en formato ticket por QZ Tray. Devuelve "qz" o lanza para que el llamador use respaldo.
export async function printCfdiTicket(cfdiId, format = "80mm", opts = {}) {
  const st = currentStation();
  const printer = opts.qzPrinter || st?.qz_printer || null;
  const layout = format === "58mm" ? "58" : "80";
  const w = Number(st?.print_width_mm) || printableOf(format);
  const r = await api.get(`/timbres/cfdi/${cfdiId}/pdf`, { params: { layout }, responseType: "arraybuffer" });
  const bytes = new Uint8Array(r.data); let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  await qzPrintPdfBase64(printer, btoa(bin), w);
  return "qz";
}

// Varios recibos en una sola operación (p. ej. nómina: uno por empleado).
export async function printTicketDocs(docs, company, format = "80mm", opts = {}) {
  const st = currentStation();
  const printer = opts.qzPrinter || st?.qz_printer || null;
  const w = Number(st?.print_width_mm) || printableOf(format);
  try {
    for (const d of docs) { await qzPrintHtml(printer, qzHtml(d, company, format, w), w); }
    return "qz";
  } catch (e) {
    warnNoQz(e);
    const body = docs.map((d) => docToInner(d, company, format)).join('<div style="page-break-after:always"></div>');
    popupPrintHTML(wrapHtml(body, widthOf(format)));
    return "popup";
  }
}

// Resuelve la impresora/cajón/ancho de la estación elegida en este equipo (POS).
export function stationOpts(config) {
  const fallback = { qzPrinter: null, qzCashDrawer: false, format: config?.ticket_format || "80mm" };
  try {
    const id = localStorage.getItem("pos_station_id");
    const st = (config?.stations || []).find((s) => s.id === id);
    if (!st) return fallback;
    return { qzPrinter: st.qz_printer ?? null, qzCashDrawer: !!st.qz_cash_drawer, format: st.printer || fallback.format };
  } catch {
    return fallback;
  }
}
